import axios from "axios";
import { toast } from "react-toastify";
import authStore from "./auth.store";

// In development, default to local backend so "Network Error" is avoided when env is not loaded
const BASE_URL =
  process.env.REACT_APP_BASE_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:8000/api/v3" : undefined);

// Request timeout in ms (default 2 min). Short timeouts can cause false "session expired" when server is slow.
const REQUEST_TIMEOUT_MS = process.env.REACT_APP_REQUEST_TIMEOUT
  ? parseInt(process.env.REACT_APP_REQUEST_TIMEOUT, 10)
  : 2 * 60 * 1000;

// Longer timeout for batch report and other heavy endpoints (default 10 min). Override with REACT_APP_BATCH_REPORT_TIMEOUT.
export const LONG_REQUEST_TIMEOUT_MS = process.env.REACT_APP_BATCH_REPORT_TIMEOUT
  ? parseInt(process.env.REACT_APP_BATCH_REPORT_TIMEOUT, 10)
  : 10 * 60 * 1000;

// Process-file / upload-to-DB can run 30+ min for large chunked files. Override with REACT_APP_PROCESS_FILE_TIMEOUT (ms).
export const PROCESS_FILE_TIMEOUT_MS = process.env.REACT_APP_PROCESS_FILE_TIMEOUT
  ? parseInt(process.env.REACT_APP_PROCESS_FILE_TIMEOUT, 10)
  : 60 * 60 * 1000; // 60 min default

const httpClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-type": "application/json",
  },
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
  timeout: REQUEST_TIMEOUT_MS,
});

// Backend should never return PII or stack traces in error.message / userMessage; use generic messages in production.
// User-facing message only: no correlation ID in toast (reference stays on payload for support if needed).
const buildUserMessage = (payload) => {
  if (payload?.message) return payload.message;
  if (payload?.err?.message) return payload.err.message;
  const error = payload?.error;
  if (error?.whatHappened) return error.whatHappened;
  return "Something went wrong while processing your request.";
};

const normalizeErrorPayload = (payload) => {
  if (!payload) return payload;
  if (payload.success !== false) return payload;
  const validationText =
    Array.isArray(payload.errors) && payload.errors.length > 0
      ? payload.errors
          .map((err) => (err && (err.msg || err.message)) || "")
          .filter(Boolean)
          .join(" ")
      : "";
  const userMessage = validationText || buildUserMessage(payload);
  return {
    ...payload,
    userMessage,
    message: userMessage,
    err: payload.err || { message: userMessage },
  };
};

httpClient.interceptors.request.use(
  function (config) {
    // Ensure headers object exists
    if (!config.headers) {
      config.headers = {};
    }

    // Default instance uses Content-Type: application/json; multipart FormData must not send that or the
    // server will not parse fields/file (multer), leading to 400 validation / "No file uploaded".
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    }

    // Get auth token from store (compatible with older Babel)
    const state = authStore.getState();
    const authToken = state && state.auth_reducer && state.auth_reducer.authToken;

    // Set token if it exists
    if (authToken) {
      config.headers["x-auth-token"] = authToken;
    }

    return config;
  },
  function (error) {
    if (process.env.NODE_ENV === "development") console.error("axios.interceptors.request error => ", error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors (token expiration)
httpClient.interceptors.response.use(
  function (response) {
    if (response && response.data && response.data.success === false) {
      response.data = normalizeErrorPayload(response.data);
      const skipToast = response.config && response.config.skipErrorToast;
      if (!skipToast && response.data.userMessage) {
        toast(response.data.userMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          type: "error",
        });
      }
    }
    // Pass through successful responses
    return response;
  },
  function (error) {
    if (error && error.response && error.response.data) {
      error.response.data = normalizeErrorPayload(error.response.data);
      error.userMessage =
        error.response &&
        error.response.data &&
        error.response.data.userMessage;
    }
    if (!error.userMessage) {
      // Network/offline: show a clear message instead of generic axios error
      const isTimeout = error.code === "ECONNABORTED" || (error.message && error.message.toLowerCase().includes("timeout"));
      const isNetworkOrTimeout = !error.response && (error.message === "Network Error" || error.code === "ECONNABORTED");
      if (isTimeout) {
        error.userMessage =
          "Request timed out. If you were processing a large file, it may still be running on the server. Refresh the batch list to check status.";
      } else if (isNetworkOrTimeout) {
        error.userMessage =
          process.env.NODE_ENV === "development"
            ? "Request failed. Ensure the backend server is running (e.g. port 8000) and reachable, or check REACT_APP_BASE_URL."
            : "Request failed. Check your connection and try again.";
      } else {
        error.userMessage = error.message || "Something went wrong while processing your request.";
      }
    }
    // Show API errors as toast (standardized display); skip when caller handles (e.g. login form)
    const skipToast = error.config && error.config.skipErrorToast;
    if (!skipToast && (!error.response || error.response.status !== 401)) {
      toast(error.userMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        type: "error",
      });
    }
    // Handle 401 Unauthorized only (expired or invalid token). Do NOT redirect on request timeout (ECONNABORTED) or network errors.
    const isTimeoutOrNetwork = !error.response && (error.code === "ECONNABORTED" || error.message === "Network Error");
    if (!isTimeoutOrNetwork && error.response && error.response.status === 401) {
      // Import auth store actions
      const authStoreModule = require("./auth.store");
      const types = authStoreModule.types;
      const actions = authStoreModule.actions;

      // Clear auth storage and reset state
      actions.removeAuthStorage();
      authStore.dispatch({ type: types.INIT_AUTH_STATE });

      // Redirect to login page
      const subdomain = process.env.REACT_APP_SUB_DOMAIN || '';
      window.location.href = subdomain + '/login';
    }

    return Promise.reject(error);
  }
);

/**
 * Download a file from an API path with auth. Uses x-auth-token from store so window.open-style links work via fetch.
 * @param {string} path - API path (e.g. /download/transactionmaster/xxx)
 * @param {string} [defaultFilename] - fallback filename if Content-Disposition is missing
 * @returns {Promise<void>}
 */
export const downloadFile = (path, defaultFilename = "download") => {
  return httpClient.get(path, { responseType: "blob" }).then((res) => {
    if (res.status < 200 || res.status >= 300) {
      return Promise.reject(new Error(res.statusText || "Download failed"));
    }
    const blob = res.data;
    const disposition = res.headers["content-disposition"];
    let filename = defaultFilename;
    if (disposition && disposition.includes("filename=")) {
      const match = disposition.match(/filename[*]?=(?:UTF-8'')?["']?([^"'\s]+)["']?/i);
      if (match && match[1]) filename = match[1].trim();
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  });
};

export default httpClient;
